import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Localization } from "../../types/types";
import { useLocalization } from "../../hooks/useLocalization";
import { useLocalePath } from "../../hooks/useLocalePath";
import { ChevronDown, ChevronUp, Menu, X } from "lucide-react";

interface HeaderProps {
  language: Localization;
  setLanguage: React.Dispatch<React.SetStateAction<Localization>>;
}

export const Header = ({ language, setLanguage }: HeaderProps) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [subMenuOpen, setSubMenuOpen] = useState(false);
  const [mobileSubMenuOpen, setMobileSubMenuOpen] = useState(false);
  const { strings } = useLocalization();
  const { goTo, changeLanguagePath, isActive, localized } = useLocalePath();
  const imgs = import.meta.env.VITE_S3_ASSET_URL;
  const flagSrc = language === Localization.Finnish ? "en-flag.png" : "fin-flag.png";
  const nextLangLabel = language === Localization.Finnish ? "English" : "Suomi";

  const closeMobileMenu = () => {
    setMenuOpen(false);
    setMobileSubMenuOpen(false);
  };

  useEffect(() => {
    if (!menuOpen) return;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  useEffect(() => {
    const onResize = () => {
      if (window.matchMedia("(min-width: 768px)").matches) {
        closeMobileMenu();
      }
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, []);

  const switchLanguage = () => {
    const nextLanguage =
      language === Localization.Finnish ? Localization.English : Localization.Finnish;
    setLanguage(nextLanguage);
    changeLanguagePath(nextLanguage);
  };

  const navigateAndClose = (path: string) => {
    goTo(path);
    closeMobileMenu();
  };

  const navLinkClass = (path: string) =>
    [
      "inline-flex items-center gap-1 pt-[0.15rem]",
      "font-shentox text-[clamp(1.05rem,1.5vw,1.3rem)] leading-[1.15]",
      "whitespace-nowrap no-underline",
      "max-[1100px]:text-[clamp(0.95rem,1.3vw,1.15rem)]",
      "hover:text-light-blue",
      isActive(path) ? "font-bold light-blue" : "font-medium text-white",
    ].join(" ");

  const dropdownLinkClass = (path: string) =>
    [
      "block w-full px-4 py-2 text-left",
      "font-shentox text-[clamp(1.05rem,1.5vw,1.3rem)] leading-[1.15]",
      "no-underline",
      "hover:text-light-blue",
      isActive(path) ? "font-bold light-blue" : "font-medium text-white",
    ].join(" ");

  const mobileLinkClass = (path?: string) =>
    [
      "relative flex w-full cursor-pointer items-center justify-center gap-[0.65rem]",
      "border-0 bg-transparent px-[2.75rem] py-[0.95rem]",
      "font-shentox text-[1.15rem] font-medium tracking-[0.06em] leading-[1.2] uppercase",
      "text-center hover:bg-white/[0.06] hover:text-light-blue",
      path && isActive(path) ? "font-bold light-blue" : "text-white",
    ]
      .filter(Boolean)
      .join(" ");

  return (
    <>
      <header
        className={`fixed left-1/2 top-[1.75rem] z-[100000] flex w-fit max-w-[calc(100vw-2rem)] -translate-x-1/2 items-center justify-evenly gap-[clamp(1.25rem,2.5vw,2rem)] rounded-round bg-white/10 px-[2.25rem] pt-[1.2rem] pb-[0.95rem] backdrop-blur-[1rem] max-[1100px]:gap-[clamp(1rem,2vw,1.5rem)] max-[1100px]:px-6 max-md:gap-[0.65rem] max-md:bg-white/[0.08] max-md:p-[0.5rem_0.65rem] ${menuOpen ? "max-md:bg-[rgba(16,16,29,0.72)]" : ""}`}
      >
        <Link
          to={localized("/")}
          className="shrink-0"
          onClick={closeMobileMenu}
          aria-label="Finnhouse home"
        >
          <img
            className="h-14 w-14 max-[1100px]:h-12 max-[1100px]:w-12 max-md:h-[2.35rem] max-md:w-[2.35rem]"
            src={`${imgs}/icons/finnhouse.png`}
            alt="Finnhouse logo"
            width={56}
            height={56}
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
        </Link>

        <nav
          className="flex flex-row flex-nowrap items-center gap-[clamp(0.85rem,1.6vw,1.35rem)] max-[1100px]:gap-[clamp(0.65rem,1.2vw,1rem)] max-md:hidden"
          aria-label="Main navigation"
        >
          <Link
            to={localized("/")}
            className={navLinkClass("/")}
            aria-current={isActive("/") ? "page" : undefined}
          >
            {strings.HEADER.HOME.toUpperCase()}
          </Link>
          <Link
            to={localized("/schedule")}
            className={navLinkClass("/schedule")}
            aria-current={isActive("/schedule") ? "page" : undefined}
          >
            {strings.HEADER.SCHEDULE.toUpperCase()}
          </Link>
          <Link
            to={localized("/results")}
            className={navLinkClass("/results")}
            aria-current={isActive("/results") ? "page" : undefined}
          >
            {strings.HEADER.RESULTS.toUpperCase()}
          </Link>

          <div
            className="relative"
            onMouseEnter={() => {
              setSubMenuOpen(true);
            }}
            onMouseLeave={() => {
              setSubMenuOpen(false);
            }}
          >
            <Link
              to={localized("/fpl/teams")}
              className={navLinkClass("/fpl")}
              aria-expanded={subMenuOpen}
              aria-haspopup="true"
              aria-current={isActive("/fpl") ? "page" : undefined}
            >
              FPL{" "}
              {subMenuOpen ? (
                <ChevronUp size={16} aria-hidden />
              ) : (
                <ChevronDown size={16} aria-hidden />
              )}
            </Link>

            {subMenuOpen ? (
              <div
                className="absolute top-[125%] z-10 min-w-[160px] rounded-[12px] border border-white/20 bg-[rgba(22,33,54,0.96)] before:absolute before:top-[-1.25rem] before:right-0 before:left-0 before:h-[1.25rem] before:content-['']"
                role="menu"
              >
                <Link
                  to={localized("/fpl/teams")}
                  className={dropdownLinkClass("/fpl/teams")}
                  role="menuitem"
                  onClick={() => {
                    setSubMenuOpen(false);
                  }}
                >
                  {strings.HEADER.TEAMS.toUpperCase()}
                </Link>
                <Link
                  to={localized("/fpl/leaderboard")}
                  className={dropdownLinkClass("/fpl/leaderboard")}
                  role="menuitem"
                  onClick={() => {
                    setSubMenuOpen(false);
                  }}
                >
                  {strings.HEADER.LEADERBOARD.toUpperCase()}
                </Link>
                <Link
                  to={localized("/fpl/stats")}
                  className={dropdownLinkClass("/fpl/stats")}
                  role="menuitem"
                  onClick={() => {
                    setSubMenuOpen(false);
                  }}
                >
                  {strings.HEADER.STATS.toUpperCase()}
                </Link>
                <Link
                  to={localized("/fpl/champions")}
                  className={dropdownLinkClass("/fpl/champions")}
                  role="menuitem"
                  onClick={() => {
                    setSubMenuOpen(false);
                  }}
                >
                  {strings.HEADER.CHAMPIONS.toUpperCase()}
                </Link>
              </div>
            ) : null}
          </div>

          <Link
            to={localized("/gallery")}
            className={navLinkClass("/gallery")}
            aria-current={isActive("/gallery") ? "page" : undefined}
          >
            {strings.HEADER.GALLERY.toUpperCase()}
          </Link>
          <Link
            to={localized("/about")}
            className={navLinkClass("/about")}
            aria-current={isActive("/about") ? "page" : undefined}
          >
            {strings.HEADER.ABOUT.toUpperCase()}
          </Link>
        </nav>

        <button
          type="button"
          className="hidden shrink-0 cursor-pointer items-center justify-center border-0 bg-transparent p-0 md:inline-flex"
          onClick={switchLanguage}
          aria-label={`Switch language to ${nextLangLabel}`}
        >
          <img
            loading="lazy"
            className="h-auto w-[2.625rem]"
            src={`${imgs}/icons/${flagSrc}`}
            alt=""
            width={42}
            height={27}
          />
        </button>

        <button
          type="button"
          className="inline-flex h-[2.35rem] w-[2.35rem] shrink-0 cursor-pointer items-center justify-center rounded-full border-0 bg-white/[0.08] text-white hover:bg-white/[0.14] hover:text-light-blue md:hidden"
          onClick={() => {
            setMenuOpen((prev) => !prev);
            if (menuOpen) setMobileSubMenuOpen(false);
          }}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
        >
          {menuOpen ? (
            <X className="block h-5 w-5 shrink-0" aria-hidden />
          ) : (
            <Menu className="block h-5 w-5 shrink-0" aria-hidden />
          )}
        </button>
      </header>

      {menuOpen ? (
        <>
          <button
            type="button"
            className="fixed inset-0 z-[99998] cursor-pointer border-0 bg-[rgba(8,8,16,0.72)] backdrop-blur-[4px]"
            aria-label="Close menu"
            onClick={closeMobileMenu}
          />
          <nav
            className="fixed top-[calc(1.75rem+3.75rem)] left-1/2 z-[99999] flex max-h-[calc(100dvh-6.5rem)] w-[calc(100%-2*var(--padding-view))] max-w-[22rem] -translate-x-1/2 flex-col items-stretch overflow-y-auto rounded-[1rem] border-0 bg-[#12121f] p-[0.35rem] shadow-[0_1.5rem_3rem_rgba(0,0,0,0.45)]"
            aria-label="Mobile navigation"
          >
            <button
              type="button"
              className={mobileLinkClass("/")}
              onClick={() => {
                navigateAndClose("/");
              }}
            >
              {strings.HEADER.HOME.toUpperCase()}
            </button>
            <button
              type="button"
              className={mobileLinkClass("/schedule")}
              onClick={() => {
                navigateAndClose("/schedule");
              }}
            >
              {strings.HEADER.SCHEDULE.toUpperCase()}
            </button>
            <button
              type="button"
              className={mobileLinkClass("/results")}
              onClick={() => {
                navigateAndClose("/results");
              }}
            >
              {strings.HEADER.RESULTS.toUpperCase()}
            </button>

            <div className="flex w-full flex-col">
              <button
                type="button"
                className={mobileLinkClass("/fpl")}
                onClick={() => {
                  setMobileSubMenuOpen((prev) => !prev);
                }}
                aria-expanded={mobileSubMenuOpen}
              >
                <span className="block w-full text-center">FPL</span>
                <span
                  className="pointer-events-none absolute top-1/2 right-4 flex h-5 w-5 -translate-y-1/2 items-center justify-center opacity-75"
                  aria-hidden
                >
                  {mobileSubMenuOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </span>
              </button>

              {mobileSubMenuOpen ? (
                <div className="mb-[0.35rem] flex flex-col gap-[0.15rem] rounded-[0.65rem] bg-white/5 p-[0.35rem]">
                  <button
                    type="button"
                    className={`w-full cursor-pointer rounded-[0.45rem] border-0 bg-transparent px-[0.85rem] py-3 text-center font-shentox text-[0.95rem] font-medium tracking-[0.05em] leading-[1.2] uppercase hover:bg-white/[0.06] hover:text-light-blue ${isActive("/fpl/teams") ? "font-bold light-blue" : "text-white/90"}`}
                    onClick={() => {
                      navigateAndClose("/fpl/teams");
                    }}
                  >
                    {strings.HEADER.TEAMS.toUpperCase()}
                  </button>
                  <button
                    type="button"
                    className={`w-full cursor-pointer rounded-[0.45rem] border-0 bg-transparent px-[0.85rem] py-3 text-center font-shentox text-[0.95rem] font-medium tracking-[0.05em] leading-[1.2] uppercase hover:bg-white/[0.06] hover:text-light-blue ${isActive("/fpl/leaderboard") ? "font-bold light-blue" : "text-white/90"}`}
                    onClick={() => {
                      navigateAndClose("/fpl/leaderboard");
                    }}
                  >
                    {strings.HEADER.LEADERBOARD.toUpperCase()}
                  </button>
                  <button
                    type="button"
                    className={`w-full cursor-pointer rounded-[0.45rem] border-0 bg-transparent px-[0.85rem] py-3 text-center font-shentox text-[0.95rem] font-medium tracking-[0.05em] leading-[1.2] uppercase hover:bg-white/[0.06] hover:text-light-blue ${isActive("/fpl/stats") ? "font-bold light-blue" : "text-white/90"}`}
                    onClick={() => {
                      navigateAndClose("/fpl/stats");
                    }}
                  >
                    {strings.HEADER.STATS.toUpperCase()}
                  </button>
                  <button
                    type="button"
                    className={`w-full cursor-pointer rounded-[0.45rem] border-0 bg-transparent px-[0.85rem] py-3 text-center font-shentox text-[0.95rem] font-medium tracking-[0.05em] leading-[1.2] uppercase hover:bg-white/[0.06] hover:text-light-blue ${isActive("/fpl/champions") ? "font-bold light-blue" : "text-white/90"}`}
                    onClick={() => {
                      navigateAndClose("/fpl/champions");
                    }}
                  >
                    {strings.HEADER.CHAMPIONS.toUpperCase()}
                  </button>
                </div>
              ) : null}
            </div>

            <button
              type="button"
              className={mobileLinkClass("/gallery")}
              onClick={() => {
                navigateAndClose("/gallery");
              }}
            >
              {strings.HEADER.GALLERY.toUpperCase()}
            </button>
            <button
              type="button"
              className={mobileLinkClass("/about")}
              onClick={() => {
                navigateAndClose("/about");
              }}
            >
              {strings.HEADER.ABOUT.toUpperCase()}
            </button>

            <button
              type="button"
              className="mt-[0.15rem] flex w-full cursor-pointer items-center justify-center gap-[0.65rem] rounded-b-[0.65rem] border-0 bg-transparent px-[2.75rem] py-[0.95rem] font-shentox text-[1rem] font-medium uppercase text-white hover:bg-white/[0.06] hover:text-light-blue"
              onClick={switchLanguage}
              aria-label={`Switch language to ${nextLangLabel}`}
            >
              <img
                loading="lazy"
                className="h-auto w-[2.625rem] shrink-0"
                src={`${imgs}/icons/${flagSrc}`}
                alt=""
                width={42}
                height={27}
              />
              <span>{nextLangLabel}</span>
            </button>
          </nav>
        </>
      ) : null}
    </>
  );
};
